"use client";

import React, { useRef, useEffect, useState } from "react";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Button } from "../../../../components/ui/button";
import { Card } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ArrowUpRight, MapPin, Sparkles, Zap, Timer, CheckCircle } from "lucide-react";

const TRAIL_SLUGS = {
  1: "founders-trail-nainital-edition-march",
  2: "founders-trail-rishikesh-edition"
};

const EnhancedFlowingBadge = ({ children, className }) => (
  <Badge className={`relative overflow-hidden border-none px-4 py-1.5 font-semibold text-xs font-jakarta shadow-md ${className}`}>
    <span className="relative z-10">{children}</span>
    <motion.div
      initial={{ x: "-150%" }}
      animate={{ x: "150%" }}
      transition={{
        repeat: Infinity,
        duration: 4,
        ease: "linear",
      }}
      className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
    />
    <motion.div
      initial={{ x: "-150%", skewX: -20 }}
      animate={{ x: "150%", skewX: -20 }}
      transition={{
        repeat: Infinity,
        duration: 3,
        ease: "linear",
        delay: 0.5
      }}
      className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/40 to-transparent w-1/2"
    />
  </Badge>
);

const HeroGlow = ({ children }) => (
  <div className="relative rounded-[2.5rem] p-[2px] bg-gradient-to-br from-[#FFAE00] via-orange-400 to-[#FFAE00] shadow-[0_0_50px_rgba(255,174,0,0.25)]">
    <div className="rounded-[2.4rem] bg-[#0A0D14] h-full overflow-hidden">
      {children}
    </div>
  </div>
);

export const TravelPackageCard = ({ pkg, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const router = useRouter();

  const isLive = String(pkg.bookingLive) === "true";
  const isTripOver = pkg.bookingLive === "Tripover";
  const isHero = pkg.id === 1;

  const Wrapper =
    isLive && isHero
      ? HeroGlow
      : ({ children }) => <div className="h-full">{children}</div>;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="h-full"
    >
      <Wrapper>
        <motion.div
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 120 }}
          className="h-full"
        >
          <Card className="relative h-full min-h-[520px] rounded-[2.4rem] overflow-hidden border-0 flex flex-col bg-white transition-all duration-500">
            <div className="relative h-[260px] w-full overflow-hidden">
              <Image
                fill
                alt={pkg.title}
                src={pkg.image || "/placeholder.svg"}
                className={`object-cover transition-all duration-[8s] ease-out hover:scale-110 brightness-[0.95] contrast-[1.05] ${isTripOver ? 'grayscale opacity-60' : 'grayscale-0'}`}
              />
              <div className="absolute top-6 left-6 flex flex-col gap-2 z-20">
                {pkg.audience && (
                  <EnhancedFlowingBadge className="bg-[#FFAE00] text-black">
                    {pkg.audience}
                  </EnhancedFlowingBadge>
                )}
                
                {isTripOver && (
                  <EnhancedFlowingBadge className="bg-green-600 text-white shadow-green-500/20">
                    <div className="flex items-center gap-1.5 font-bold">
                      <CheckCircle size={14} className="text-white" />
                      COMPLETED
                    </div>
                  </EnhancedFlowingBadge>
                )}
              </div>
              
              {isLive && pkg.spotsLeft && (
                <div className="absolute top-6 right-6 z-20">
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <EnhancedFlowingBadge className="bg-white/95 backdrop-blur-sm text-red-600 border border-red-200 py-1.5 px-3 flex items-center gap-2 shadow-xl">
                      <div className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                      </div>
                      <span className="font-bold tracking-tighter uppercase text-[10px] sm:text-xs">
                        {pkg.spotsLeft} Spots Left
                      </span>
                    </EnhancedFlowingBadge>
                  </motion.div>
                </div>
              )}
            </div>

            <div className="relative mx-4 -mt-20 p-5 md:p-6 rounded-[2rem] bg-white/90 backdrop-blur-xl border border-slate-200 shadow-[0_30px_80px_rgba(0,0,0,0.08)] flex flex-col flex-grow transition-all duration-500">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[#FFAE00]" />
                <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#FFAE00]">
                  {pkg.details}
                </span>
              </div>

              <h3 className="text-xl md:text-2xl font-bold mb-3 tracking-tight">
                {pkg.title}
              </h3>

              <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-4">
                <div className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-lg bg-slate-100 text-slate-600">
                  <MapPin size={12} /> {pkg.date}
                </div>

                <div className="flex items-center gap-1.5 text-[10px] font-bold opacity-60 uppercase">
                  <Timer size={12} /> 4 Days
                </div>
              </div>

              <p className="text-[14px] leading-relaxed mb-6 text-slate-600 line-clamp-3">
                {pkg.description}
              </p>
           
              <div className="mt-auto pt-5 border-t border-slate-200 flex items-center justify-between gap-4">
                <Button
                  className={`h-11 md:h-12 w-full px-4 md:px-6 rounded-2xl font-bold text-xs md:text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                    isTripOver
                      ? "bg-slate-800 text-white hover:bg-slate-700"
                      : "bg-gradient-to-r from-[#FFAE00] to-orange-400 text-black hover:shadow-[0_0_30px_rgba(255,174,0,0.5)]"
                  }`}
                  onClick={() => {
                    if (isTripOver) router.push(`/blog/${pkg.slug}`);
                    else
                      window.open(
                        "https://waitlist.outcomeouting.com/",
                        "_blank"
                      );
                  }}
                >
                  <span className="truncate">
                    {isTripOver ? "Read Blog" : "Join the Waitlist"}
                  </span>
                  <ArrowUpRight size={18} className="shrink-0" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </Wrapper>
    </motion.div>
  );
};

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get("/api/trails");
        const sortedData = response.data.sort((a, b) => {
          const order = { "true": 0, "false": 1, "Tripover": 2 };
          const aStatus = String(a.bookingLive);
          const bStatus = String(b.bookingLive);
          const aOrder = order[aStatus] ?? 99;
          const bOrder = order[bStatus] ?? 99;
          
          if (aOrder !== bOrder) return aOrder - bOrder;
          return (Number(a.id) || 99) - (Number(b.id) || 99);
        });
        setPackages(sortedData);
        setFilter("upcoming");
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setTimeout(() => setLoading(false), 800);
      }
    };
    fetchPackages();
  }, []);

  const filteredPackages = packages.filter((pkg) => {
    const status = String(pkg.bookingLive);
    if (filter === "upcoming") return status === "true" || status === "false";
    if (filter === "past") return status === "Tripover";
    return true;
  });

  const tabs = [
    { id: "upcoming", label: "Upcoming & Live" },
    { id: "past", label: "Past Trips" },
  ];

  return (
    <section id="travelPackagesSection" className="py-12 md:py-24 min-h-screen relative overflow-hidden bg-white w-full">
      <div className="absolute top-0 left-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full blur-[100px] bg-[#FFAE00]/5" />
      <div className="absolute bottom-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-blue-500/5 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 flex flex-col items-center">
        <header className="mb-12 md:mb-20 text-center">
          <h2 className="text-5xl md:text-8xl font-bricolage font-black tracking-tighter text-slate-900 leading-[1] md:leading-[0.9]">
            Trips <span className="text-[#FFAE00]">That</span> Transform
          </h2>
          <p className="text-slate-500 mt-4 md:mt-6 text-base md:text-lg font-medium italic">
          &quot;Designed to unlock clarity and connection&quot;
          </p>

          <div className="flex bg-gray-100 p-1 rounded-full mt-10 border border-black/5 relative overflow-hidden w-full max-w-[320px] sm:max-w-md mx-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`flex-1 relative z-10 py-2 sm:py-2.5 rounded-full text-[11px] sm:text-sm font-bold font-bricolage transition-colors duration-300 ${
                  filter === tab.id ? "text-white" : "text-black/40 hover:text-black"
                }`}
              >
                {tab.label}
                {filter === tab.id && (
                  <motion.div
                    layoutId="activePill"
                    className="absolute inset-0 bg-[#FFAE00] rounded-full -z-10 shadow-sm"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </button>
            ))}
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 w-full">
          <AnimatePresence mode="popLayout">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div
                  key={i}
                  className="h-[520px] w-full bg-slate-50 animate-pulse rounded-[2.4rem]"
                />
              ))
            ) : (
              filteredPackages.map((pkg, i) => (
                <TravelPackageCard key={pkg.id} pkg={pkg} index={i} />
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}